import { useMemo, useState } from "react";
import {
    Dialog, DialogActions, DialogContent, DialogTitle, Icon, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { formatarNumero } from "./analiseUtils";
import { baixarCsv as baixar, decimalCsv } from "./csv";

const formatoPercentual = new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Janela "Ver detalhes" da Participação por Cliente: a tabela completa, ninguém escondido. Agrupa por cliente (cada
 * pessoa que o atendeu, com as fichas e o % do cliente) ou por pessoa (os clientes dela). Base = fichas acionadas mês a
 * mês, a mesma do gráfico. As pessoas em destaque no gráfico ficam marcadas. "Baixar CSV" leva as linhas da tela.
 */
export default function DetalheParticipacao({ participacoes, pessoas, cores, destacados, rotuloPessoa, onFechar }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = coresDoTema(darkMode);
    const [agrupar, setAgrupar] = useState("cliente");
    const [busca, setBusca] = useState("");

    const totalGeral = participacoes.reduce((acc, p) => acc + p.total, 0);
    const porCliente = agrupar === "cliente";

    const grupos = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const casa = (nome) => !termo || nome.toLowerCase().includes(termo);
        if (porCliente) {
            return participacoes.map(p => ({
                chave: p.cliente,
                nome: p.cliente,
                fichas: p.total,
                parte: null,
                linhas: [...p.partes]
                    .sort((a, b) => b.quantidade - a.quantidade || a.nome.localeCompare(b.nome))
                    .filter(parte => casa(parte.nome))
                    .map(parte => ({ cliente: p.cliente, pessoa: parte.nome, fichas: parte.quantidade, percentual: parte.percentual })),
            })).filter(g => g.linhas.length > 0);
        }
        return pessoas.filter(casa).map(nome => {
            const linhas = participacoes
                .map(p => {
                    const parte = p.partes.find(x => x.nome === nome);
                    return parte ? { cliente: p.cliente, pessoa: nome, fichas: parte.quantidade, percentual: parte.percentual } : null;
                })
                .filter(Boolean)
                .sort((a, b) => b.fichas - a.fichas);
            const fichas = linhas.reduce((acc, l) => acc + l.fichas, 0);
            return { chave: nome, nome, fichas, parte: totalGeral > 0 ? fichas / totalGeral : 0, linhas };
        }).filter(g => g.linhas.length > 0);
    }, [participacoes, pessoas, porCliente, busca, totalGeral]);

    const baixarCsv = () => baixar("participacao_por_cliente.csv", [
        ["Cliente", rotuloPessoa, "Fichas acionadas (mês a mês)", "% do cliente"],
        ...grupos.flatMap(g => g.linhas).map(l => [l.cliente, l.pessoa, l.fichas, decimalCsv(l.percentual * 100)]),
    ]);

    const celula = {
        display: "table-cell", py: 0.6, px: 1.25, fontSize: "0.8rem", color: `${tema.texto} !important`,
        borderBottom: `1px solid ${tema.divisoria}`, verticalAlign: "middle",
    };
    const cabecalho = { ...celula, fontWeight: "bold", color: `${tema.textoSecundario} !important`, fontSize: "0.75rem", background: `${tema.painel} !important` };
    const linhaGrupo = { ...celula, fontWeight: "bold", background: tema.painel };
    const bolinha = (nome) => (
        <MDBox component="span" sx={{ display: "inline-block", width: 9, height: 9, minWidth: 9, borderRadius: "50%", background: cores.cor(nome), mr: 1 }} />
    );
    const botaoAgrupar = (valor, texto) => (
        <MDButton variant={agrupar === valor ? "contained" : "outlined"} color="info" size="small" onClick={() => setAgrupar(valor)}>
            {texto}
        </MDButton>
    );

    return (
        <Dialog open onClose={onFechar} maxWidth="md" fullWidth PaperProps={{ sx: { background: tema.cartao, color: tema.texto } }}>
            <DialogTitle sx={{ pb: 1, color: `${tema.texto} !important` }}>Participação nos Atendimentos por Cliente: detalhes</DialogTitle>
            <DialogContent sx={{ pt: "4px !important" }}>
                <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1.5}>
                    <MDBox display="flex" alignItems="center" gap={1} sx={{ color: "inherit" }}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">Agrupar por</MDTypography>
                        {botaoAgrupar("cliente", "Cliente")}
                        {botaoAgrupar("pessoa", rotuloPessoa)}
                    </MDBox>
                    <MDBox sx={{ width: 240 }}>
                        <MDInput
                            size="small"
                            fullWidth
                            placeholder={`Buscar ${rotuloPessoa.toLowerCase()}...`}
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon fontSize="small" sx={{ color: `${tema.textoSecundario} !important` }}>search</Icon>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </MDBox>
                    <MDBox flex={1} />
                    <MDButton variant="outlined" color="info" size="small" onClick={baixarCsv} disabled={!grupos.length}>
                        <Icon sx={{ mr: 0.5 }}>download</Icon> Baixar CSV
                    </MDButton>
                </MDBox>
                <TableContainer sx={{ boxShadow: "none", background: "transparent", borderRadius: 0, maxHeight: 480 }}>
                    <Table size="small" stickyHeader sx={{ display: "table" }}>
                        <TableHead sx={{ display: "table-header-group" }}>
                            <TableRow sx={{ display: "table-row" }}>
                                <TableCell sx={cabecalho}>{porCliente ? `Cliente / ${rotuloPessoa}` : `${rotuloPessoa} / Cliente`}</TableCell>
                                <TableCell align="right" sx={cabecalho}>Fichas acionadas (mês a mês)</TableCell>
                                <TableCell sx={{ ...cabecalho, width: "40%" }}>% do cliente</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ display: "table-row-group" }}>
                            {grupos.map(g => (
                                [
                                    <TableRow key={`g-${g.chave}`} sx={{ display: "table-row" }}>
                                        <TableCell sx={linhaGrupo}>
                                            <MDBox display="flex" alignItems="center" sx={{ color: "inherit" }}>
                                                {!porCliente && bolinha(g.nome)}
                                                {g.nome}
                                            </MDBox>
                                        </TableCell>
                                        <TableCell align="right" sx={linhaGrupo}>{formatarNumero(g.fichas)}</TableCell>
                                        <TableCell sx={{ ...linhaGrupo, fontWeight: 400, color: `${tema.textoSecundario} !important`, fontSize: "0.75rem" }}>
                                            {porCliente ? "100% do cliente" : `${formatoPercentual.format(g.parte)} de todas as fichas`}
                                        </TableCell>
                                    </TableRow>,
                                    ...g.linhas.map(l => {
                                        const ativo = destacados.has(l.pessoa);
                                        return (
                                            <TableRow key={`${g.chave}-${porCliente ? l.pessoa : l.cliente}`} sx={{ display: "table-row", background: ativo ? tema.hover : "transparent" }}>
                                                <TableCell sx={{ ...celula, pl: 3, fontWeight: ativo ? "bold" : 400 }}>
                                                    <MDBox display="flex" alignItems="center" sx={{ color: "inherit" }}>
                                                        {porCliente ? <>{bolinha(l.pessoa)}{l.pessoa}</> : l.cliente}
                                                    </MDBox>
                                                </TableCell>
                                                <TableCell align="right" sx={celula}>{formatarNumero(l.fichas)}</TableCell>
                                                <TableCell sx={celula}>
                                                    <MDBox display="flex" alignItems="center" gap={1} sx={{ color: "inherit" }}>
                                                        <MDBox sx={{ flex: 1, height: 8, borderRadius: 4, background: tema.divisoria }}>
                                                            <MDBox sx={{ width: `${l.percentual * 100}%`, height: 8, borderRadius: 4, background: cores.cor(l.pessoa) }} />
                                                        </MDBox>
                                                        <MDBox component="span" sx={{ width: 52, textAlign: "right", color: "inherit" }}>{formatoPercentual.format(l.percentual)}</MDBox>
                                                    </MDBox>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }),
                                ]
                            ))}
                            {grupos.length === 0 && (
                                <TableRow sx={{ display: "table-row" }}>
                                    <TableCell colSpan={3} align="center" sx={celula}>Nenhum {rotuloPessoa.toLowerCase()} com &quot;{busca}&quot;.</TableCell>
                                </TableRow>
                            )}
                            <TableRow sx={{ display: "table-row" }}>
                                <TableCell sx={{ ...celula, fontWeight: "bold" }}>Total geral</TableCell>
                                <TableCell align="right" sx={{ ...celula, fontWeight: "bold" }}>{formatarNumero(totalGeral)}</TableCell>
                                <TableCell sx={celula} />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1.5, opacity: 0.85 }}>
                    Fichas acionadas mês a mês: a mesma ficha acionada em dois meses conta duas vezes; é a base do % (cada cliente soma
                    100%). As pessoas em destaque no gráfico aparecem marcadas.
                </MDTypography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <MDButton variant="outlined" color="info" onClick={onFechar}>Fechar</MDButton>
            </DialogActions>
        </Dialog>
    );
}
